/// <reference path="../_references.ts"/>


module App.Customer {
	import Customer = App.Customer.Customer;
	import Location = App.DispatchService.Location;

	interface LocalStorage extends ng.local.storage.ILocalStorageService {
		selectedCustomerId: number;
	}

	export class SelectedCustomer implements Customer {
		id: number = null;
		priority: number = 3;
		full_name: string = null;
		company_name: string = null;
		service_addresses: Array<Address> = [];
		first_name: string = null;
		last_name: string = null;
		contract_rate: boolean = false;
		emails: Array<Email> = [];
		phone_numbers: Array<PhoneNumber> = [];
		billing_addresses: Array<Address> = [];
		able_to_bill: number = 0;
		notes: string;
		rate_type: number = null;
		can_show_financing: boolean = true;

		searchQuery: string;
		searchResults: Customer[];

		private onChangeCallbacks: Array<(data?: Customer) => any> = [];

		private isDataSet = false;
		private loading: number = null;

		public isFetching: boolean = false;
		public pageSize: number = 10;
		public page: number = 1;
		public count: number = 0;
		public maxPage: number = 1;

		constructor(
			private $localStorage: LocalStorage,
			private CustomerService: CustomerService,
		) {

		}

		clearSearchResults(): void {
			const vm = this;

			vm.searchResults = [];
			vm.searchQuery = '';
		}

		setSearchResultsCache(results: Customer[]) {
			const vm = this;

			vm.searchResults = results;
		}

		set(customer: Customer): void | ng.IPromise<boolean> {
			let vm = this;

			vm.$localStorage.selectedCustomerId = customer.id;

			vm.id = customer.id;
			vm.priority = customer.priority;
			vm.full_name = customer.full_name;
			vm.company_name = customer.company_name;
			vm.service_addresses = customer.service_addresses;
			vm.first_name = customer.first_name;
			vm.last_name = customer.last_name;
			vm.contract_rate = customer.contract_rate;
			vm.emails = customer.emails;
			vm.phone_numbers = customer.phone_numbers;
			vm.billing_addresses = customer.billing_addresses;
			vm.able_to_bill = customer.able_to_bill;
			vm.notes = customer.notes;
			vm.rate_type = customer.rate_type;
			vm.can_show_financing = customer.can_show_financing;

			vm.isDataSet = true;

			angular.forEach(vm.onChangeCallbacks, (callback) => {
				callback(vm);
			});
		}

		clear(): void {
			let vm = this;

			vm.id = null;
			vm.priority = 3;
			vm.full_name = null;
			vm.company_name = null;
			vm.service_addresses = [];
			vm.first_name  = null;
			vm.last_name = null;
			vm.contract_rate = false;
			vm.emails = [];
			vm.phone_numbers = [];
			vm.billing_addresses = [];
			vm.able_to_bill = 0;
			vm.notes = '';
			vm.rate_type = null;

			vm.isDataSet = false;

			vm.$localStorage.selectedCustomerId = null;
		}

		isSet(): boolean {
			return this.isDataSet;
		}

		loadServiceAddreses(query?: string) {
			this.isFetching = true;

			if (query) {
				this.page = 1;
			}

			return this.CustomerService.getServiceAddressList(this.$localStorage.selectedCustomerId, {
				page: this.page,
				size: this.pageSize,
				query: query
			}).then((response) => {
				let headers = response.headers();

				if (this.updatePageInfo(Number(headers.count))) {
					this.page = this.maxPage;
				}

				this.service_addresses = response.data;
			}).finally(() => {
				this.isFetching = false;
			});
		}

		setPage(page: number) {
			let newPage = Math.max(1, Math.min(this.maxPage, page));
			if (newPage !== this.page) {
				this.page = newPage;
				this.loadServiceAddreses();
			}
		}

		getItemCount() {
			if (this.count === 0) {
				return '0 - 0 of 0';
			}

			return Math.min(this.count, ((this.page - 1) * this.pageSize) + 1) + ' - ' + Math.min(this.count, this.pageSize * this.page) + ' of ' + this.count;
		}

		updatePageInfo(count: number) {
			this.count = count;
			this.maxPage = Math.max(1, Math.ceil(this.count / this.pageSize));

			return this.maxPage < this.page;
		}

		loadFromStorage(): void | ng.IPromise<boolean> {
			const vm = this;

			if (vm.$localStorage.selectedCustomerId !== null && angular.isDefined(vm.$localStorage.selectedCustomerId)) {
				const id = vm.$localStorage.selectedCustomerId;
				if (vm.loading === id) {
					return;
				}

				vm.loading = id;

				return vm.CustomerService.fetch(id, { show_service_address: false }).then((response) => {
					vm.set(response.data);
					vm.loadServiceAddreses();
					vm.loading = null;
					return true;
				});
			}
		}

		addCallback(callback: (data?: Customer) => any) {
			this.onChangeCallbacks.push(callback);
		}

		addServiceAddress(address: Address) {
			const vm = this;

			vm.service_addresses.push(address);
			return vm.save();
		}

		addBillingAddress(address: Address) {
			const vm = this;

			vm.billing_addresses.push(address);
			vm.save();
		}

		save() {
			const vm = this;

			let customer = new Customer();

			customer.id = vm.id;
			customer.priority = vm.priority;
			customer.full_name = vm.full_name;
			customer.company_name = vm.company_name;
			customer.service_addresses = vm.service_addresses;
			customer.first_name = vm.first_name;
			customer.last_name = vm.last_name;
			customer.contract_rate = vm.contract_rate;
			customer.emails = vm.emails;
			customer.phone_numbers = vm.phone_numbers;
			customer.billing_addresses = vm.billing_addresses;
			customer.able_to_bill = vm.able_to_bill;
			customer.notes = vm.notes;
			customer.rate_type = vm.rate_type;

			vm.loading = vm.id;

			return vm.CustomerService.update(customer).then((response) => {
				vm.set(response.data);
				vm.loading = null;
				return true;
			});
		}

		getServiceAddressLocation(serviceAddressId: number) {
			const vm = this;
			let location: Location;

			for(let i = 0, len = vm.service_addresses.length; i < len; i += 1) {
				let address = vm.service_addresses[i];

				if(address.id === serviceAddressId) {
					location = address.location;
					break;
				}
			}

			return location;
		}

		public static Factory() {
			let instance = ($localStorage: LocalStorage, CustomerService: CustomerService) => {
				return new SelectedCustomer($localStorage, CustomerService);
			};

			instance.$inject = ['$localStorage', 'CustomerService'];

			return instance;
		}
	}
}